import { Button, H2, P, TextLink } from '@manulife/mux';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { TransferOutWithToken, DESTINATIONS } from '../Shared/ReducerActions/TransferOutWithToken';
import { AddButtonWrapper } from '../Shared/SharedStyles';
import { canAddFamily } from './utils';

const ManageFamilyActions = () => {
    const {
        features,
        memberBenefits,
        familyMembers,
        allowedDependentActions,
        studentMaxAge,
        childDependentMaxAge: studentMinAge,
    } = useShallowEqualSelector((state) => ({
        features: state.Features.data,
        member: state.MemberData.member,
        memberBenefits: state.MemberData.memberBenefits,
        familyMembers: state.MemberData.familyMembers,
        allowedDependentActions: state.MemberData.allowedDependentActions,
        studentMaxAge: state.MemberData.member.plan.studentMaxAge,
        childDependentMaxAge: state.MemberData.member.plan.childDependentMaxAge,
    }));
    const history = useHistory();
    const dispatch = useDispatch();

    const manageLifeEventOnMmb = features['lifeEvent'] || features['lifeEventHold'];

    // Determines if add family section should be displayed
    const shouldDisplayAddFamilySection = useMemo(
        () =>
            !manageLifeEventOnMmb &&
            canAddFamily(
                allowedDependentActions,
                features,
                familyMembers,
                memberBenefits,
                studentMaxAge,
                studentMinAge
            ),
        [
            manageLifeEventOnMmb,
            allowedDependentActions,
            features,
            familyMembers,
            memberBenefits,
            studentMaxAge,
            studentMinAge,
        ]
    );

    // Determines if update family (via MMB) section should be displayed
    const shouldDisplayUpdateFamilySection = features['lifeEvent'] && features['COB'] && features['studentStatus'];

    const addFamilyMemberHeader = useAEMContent(FRAGMENT_MP.landing, 'addFamilyMemberHOne');
    const manageFamilyMemberHeader = useAEMContent(FRAGMENT_MP.landing, 'manageFamilyMemberHOne');
    const addFamilyMembersText = useAEMContent(FRAGMENT_MP.landing, 'familyMembersText');
    const updateFamilyMembersText = useAEMContent(FRAGMENT_MP.landing, 'updateFamilyMembersText');
    const whosCovered = useAEMContent(FRAGMENT_MP.landing, 'whosCovered');

    const addButton = useAEMContent(FRAGMENT_MP.common, 'addButton');
    const updateButton = useAEMContent(FRAGMENT_MP.common, 'updateButton');

    const manageFamilyMemberPath = useAEMContent(FRAGMENT_MP.familyMember, 'mmbLifeEventPath');

    return (
        <>
            {shouldDisplayAddFamilySection && (
                <>
                    <H2>{addFamilyMemberHeader}</H2>
                    <P>
                        {addFamilyMembersText}
                        <TextLink
                            to="/gb/member-profile-2/family/whoiseligible"
                            as={Link}
                            data-testid="whoIsEligibleUpdateButton"
                            data-analytics-click="who-is-eligible-update"
                        >
                            {whosCovered}
                        </TextLink>
                    </P>
                    <AddButtonWrapper>
                        <Button
                            variant={Button.VARIANT.SECONDARY}
                            onClick={() => history.push('/gb/member-profile-2/family/addfamily')}
                            id="addFamilyButton"
                            data-testid="addFamilyButton"
                            data-analytics-click="add-family"
                            style={{}}
                        >
                            {addButton}
                        </Button>
                    </AddButtonWrapper>
                </>
            )}

            {shouldDisplayUpdateFamilySection && (
                <>
                    <H2>{manageFamilyMemberHeader}</H2>
                    <P>
                        {updateFamilyMembersText}
                        <TextLink
                            to="/gb/member-profile-2/family/whoiseligible"
                            as={Link}
                            data-testid="whoIsEligibleUpdateButton"
                            data-analytics-click="who-is-eligible-update"
                        >
                            {whosCovered}
                        </TextLink>
                    </P>
                    <AddButtonWrapper>
                        <Button
                            variant={Button.VARIANT.SECONDARY}
                            onClick={() =>
                                dispatch(
                                    TransferOutWithToken(
                                        DESTINATIONS.manageMyBenefits,
                                        manageFamilyMemberPath,
                                        '',
                                        '_self'
                                    )
                                )
                            }
                            id="updateFamilyButton"
                            data-testid="updateFamilyButton"
                            data-analytics-click="update-family"
                        >
                            {updateButton}
                        </Button>
                    </AddButtonWrapper>
                </>
            )}
        </>
    );
};

export default ManageFamilyActions;
